import React, { useEffect, useState } from "react";
import { apiFetch } from "../utils/api";
import BuscadorCatalogo, { coincideBusqueda } from "./BuscadorCatalogo";
import Cargando from "./Cargando";

// Matriz área x categoría — qué categorías de proveedor puede ver cada área
// al recomendar proveedor en una solicitud. Si un área no tiene ninguna
// casilla marcada, sus solicitantes ven TODOS los proveedores (sin filtro).
function VisibilidadCategorias() {
  const [areas, setAreas] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [mapa, setMapa] = useState({}); // { [area_id]: Set<categoria_id> }
  const [cargando, setCargando] = useState(true);
  const [guardandoArea, setGuardandoArea] = useState(null);
  const [error, setError] = useState("");
  const [busqueda, setBusqueda] = useState("");

  const cargar = () => {
    setCargando(true);
    Promise.all([
      apiFetch("/api/areas"),
      apiFetch("/api/categorias-proveedor"),
      apiFetch("/api/categorias-proveedor/visibilidad"),
    ])
      .then(([areasData, categoriasData, visibilidadData]) => {
        setAreas(areasData || []);
        setCategorias(categoriasData || []);
        const m = {};
        (visibilidadData || []).forEach((v) => {
          if (!m[v.area_id]) m[v.area_id] = new Set();
          m[v.area_id].add(v.categoria_id);
        });
        setMapa(m);
      })
      .catch((err) => setError(err.message))
      .finally(() => setCargando(false));
  };

  useEffect(cargar, []);

  const alternar = async (areaId, categoriaId) => {
    const actuales = mapa[areaId] || new Set();
    const nuevas = new Set(actuales);
    nuevas.has(categoriaId) ? nuevas.delete(categoriaId) : nuevas.add(categoriaId);
    setMapa((prev) => ({ ...prev, [areaId]: nuevas }));

    setGuardandoArea(areaId);
    try {
      await apiFetch("/api/categorias-proveedor/visibilidad", {
        method: "POST",
        body: JSON.stringify({ area_id: areaId, categoria_ids: [...nuevas] }),
      });
    } catch (err) {
      setError(err.message);
      cargar(); // algo falló — recarga para no dejar la casilla desincronizada
    } finally {
      setGuardandoArea(null);
    }
  };

  if (cargando) return <Cargando />;

  if (categorias.length === 0) {
    return (
      <p style={{ marginTop: "0.75rem", color: "#5b6472" }}>
        Todavía no hay categorías creadas — agregá alguna arriba primero.
      </p>
    );
  }

  const areasVisibles = areas.filter((a) => coincideBusqueda(busqueda, [a.nombre]));

  return (
    <div style={styles.card}>
      <div style={styles.cardHeader}>
        <span style={styles.cardIcono}>👁️</span>
        <div style={{ textAlign: "center" }}>
          <h3 style={{ margin: 0, fontSize: "1rem", color: "var(--tema-titulo, #0b3c60)" }}>Qué categorías ve cada área</h3>
          <p style={{ margin: "0.1rem 0 0", fontSize: "0.78rem", color: "#8993a1" }}>
            Marca las categorías de proveedor que puede recomendar cada área. Un área sin ninguna
            marca ve todos los proveedores.
          </p>
        </div>
      </div>

      {error && <p style={{ color: "#a3402f" }}>{error}</p>}

      <BuscadorCatalogo
        valor={busqueda}
        onCambiar={setBusqueda}
        placeholder="Buscar área…"
        mostrados={areasVisibles.length}
        total={areas.length}
      />

      <div className="table-scroll" style={styles.tablaWrap}>
        <table style={styles.tabla}>
          <thead>
            <tr style={{ borderBottom: "1px solid #d3d9df" }}>
              <th style={{ ...styles.th, textAlign: "left" }}>Área</th>
              {categorias.map((c) => (
                <th key={c.id} style={styles.th}>
                  {c.nombre}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {areasVisibles.map((a, i) => (
              <tr
                key={a.id}
                style={{ borderBottom: "1px solid #eef1f3", background: i % 2 === 1 ? "#fafbfc" : "transparent" }}
              >
                <td style={{ ...styles.td, fontWeight: 600, color: "var(--tema-titulo, #0b3c60)" }}>
                  {a.nombre}
                  {guardandoArea === a.id && (
                    <span style={{ marginLeft: "0.4rem", fontSize: "0.72rem", color: "#8993a1", fontWeight: 400 }}>
                      guardando...
                    </span>
                  )}
                </td>
                {categorias.map((c) => (
                  <td key={c.id} style={{ ...styles.td, textAlign: "center" }}>
                    <input
                      type="checkbox"
                      checked={!!mapa[a.id]?.has(c.id)}
                      onChange={() => alternar(a.id, c.id)}
                    />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const styles = {
  card: {
    border: "1px solid #e2e6ea",
    borderRadius: 14,
    padding: "1.1rem 1.2rem",
    marginTop: "1.25rem",
    background: "#fff",
    boxShadow: "0 1px 3px rgba(27,42,74,0.06)",
  },
  cardHeader: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "0.4rem",
    marginBottom: "1rem",
    textAlign: "center",
  },
  cardIcono: {
    width: 34,
    height: 34,
    borderRadius: 9,
    background: "var(--tema-suave, #dceaf5)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "1.1rem",
    flexShrink: 0,
  },
  tablaWrap: { border: "1px solid #eef1f3", borderRadius: 10 },
  tabla: { width: "100%", borderCollapse: "collapse", fontSize: "0.85rem" },
  th: {
    padding: "0.55rem 0.5rem",
    background: "#eef1f3",
    border: "1px solid #d3d9df",
    fontSize: "0.75rem",
    fontWeight: 700,
    color: "var(--tema-titulo, #0b3c60)",
    whiteSpace: "nowrap",
  },
  td: { padding: "0.5rem 0.6rem" },
};

export default VisibilidadCategorias;
